import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL, STATUS_FLOW } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { FileText, CheckCircle2, XCircle, Clock, Receipt, TrendingUp, Package } from "lucide-react";

const COLORS = ["#EA580C", "#0284C7", "#16A34A", "#DC2626", "#F59E0B", "#0891B2", "#65A30D", "#78716C", "#6366F1", "#10B981", "#94A3B8"];

// Chart sizing & typography
const CHART_HEIGHT_PX = 260;
const LEGEND_FONT_SIZE_PX = 11;
// Pre-allocate prop literals — passing a new array on every render would force
// Recharts' memoized chart elements to re-compute layout.
const BAR_RADIUS = [4, 4, 0, 0];
const PIE_INNER_RADIUS = 50;
const PIE_OUTER_RADIUS = 80;
const LEGEND_WRAPPER_STYLE = { fontSize: LEGEND_FONT_SIZE_PX };

// Static lookup — defined at module scope so it isn't re-allocated on every Kpi render.
const KPI_TONES = {
  orange: "bg-orange-50 text-orange-700",
  blue: "bg-blue-50 text-blue-700",
  green: "bg-green-50 text-green-700",
  red: "bg-red-50 text-red-700",
  amber: "bg-amber-50 text-amber-700",
  slate: "bg-slate-100 text-slate-700",
};

function Kpi({ label, value, icon: Icon, tone = "orange", testId }) {
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="heading text-3xl font-bold text-slate-900 mt-1">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-md ${KPI_TONES[tone]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch(() => {
        // Non-fatal: the loading skeleton stays visible; the user can refresh.
      });
  }, []);

  if (!stats) return <div className="text-slate-500">Loading dashboard…</div>;

  const statusData = STATUS_FLOW
    .map((s) => ({ name: s, value: stats.by_status?.[s] || 0 }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="heading text-3xl font-bold text-slate-900">{ROLE_LABEL[user.role]} Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back, {user.name}. Here&apos;s your operations snapshot.</p>
        </div>
        {user.role === "customer" && (
          <Link to="/returns/new" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="dash-new-request">
            + New Return Request
          </Link>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi label="Total" value={stats.total} icon={FileText} tone="slate" testId="kpi-total" />
        <Kpi label="Open" value={stats.open} icon={Clock} tone="amber" testId="kpi-open" />
        <Kpi label="Approved" value={stats.approved} icon={CheckCircle2} tone="green" testId="kpi-approved" />
        <Kpi label="Rejected" value={stats.rejected} icon={XCircle} tone="red" testId="kpi-rejected" />
        <Kpi label="Credit Notes" value={stats.credit_notes_issued} icon={Receipt} tone="orange" testId="kpi-credit-notes" />
        <Kpi label="Closed" value={stats.closed} icon={Package} tone="blue" testId="kpi-closed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="heading text-base">Monthly Returns</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: CHART_HEIGHT_PX }}>
              <ResponsiveContainer>
                <BarChart data={stats.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#EA580C" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="heading text-base">Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-16">No data yet</div>
            ) : (
              <div style={{ width: "100%", height: CHART_HEIGHT_PX }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={PIE_INNER_RADIUS} outerRadius={PIE_OUTER_RADIUS}>
                      {statusData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="heading text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-600" /> Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_activity.length === 0 ? (
            <div className="text-sm text-slate-500">No activity yet.</div>
          ) : (
            <ul className="space-y-2">
              {stats.recent_activity.map((a, i) => (
                <li key={`${a.timestamp}-${a.actor_email}-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-xs w-36 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                    <span className="text-slate-700">{a.action} on <span className="font-medium">{a.entity}</span></span>
                    {a.new_value && typeof a.new_value === "string" && <StatusBadge status={a.new_value} />}
                  </div>
                  <span className="text-xs text-slate-500">{a.actor_email}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
